import {
  FloatingFocusManager,
  autoUpdate,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CancelClose, ChevronDown, Search } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { CheckBox, ColorType, FormInput, Icon, Label, LabelType, Scrollable } from '../../_ui';

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  canHide: boolean;
}

interface ChargerColumnsDropdownProps {
  columns: ColumnConfig[];
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  onReset: () => void;
}

export const ChargerColumnsDropdown = memo(
  ({ columns, onColumnVisibilityChange, onReset }: ChargerColumnsDropdownProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchStr, setSearchStr] = useState('');

    const filteredColumns = columns.filter((col) =>
      col.label.toLowerCase().includes(searchStr.toLowerCase()),
    );

    const { refs, floatingStyles, context } = useFloating({
      placement: 'bottom-start',
      open: isOpen,
      onOpenChange: (val) => {
        setIsOpen(val);
        if (!val) setSearchStr('');
      },
      middleware: [offset(10)],
      whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

    const handleToggle = useCallback(
      (columnId: string, currentVisibility: boolean) => {
        onColumnVisibilityChange(columnId, !currentVisibility);
      },
      [onColumnVisibilityChange],
    );
    const visibleCount = columns.filter((col) => col.visible).length;

    return (
      <>
        <button
          ref={refs.setReference}
          {...getReferenceProps()}
          className={`px-3 py-2 rounded flex items-center gap-2 w-[120px] ${
            isOpen ? 'bg-grey2' : 'bg-grey1 hover:bg-grey2'
          }`}
          type='button'
        >
          <Label text={t('columns')} type={LabelType.DROPDOWN_HEADER} />
          <Icon
            src={ChevronDown}
            className={`ml-2 transition-transform duration-150 ${
              isOpen ? 'rotate-180' : 'rotate-0'
            }`}
          />
        </button>

        {isOpen && (
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className='z-50 bg-white rounded-lg shadow-lg border border-grey2 w-[280px]'
            >
              <div className='p-3 border-b border-grey2'>
                <FormInput
                  width='100%'
                  placeholder={t('search')}
                  icon={Search}
                  iconAlt={t('search')}
                  iconSize={IconSize.SIZE_20x20}
                  onChange={(e) => setSearchStr(e.target.value)}
                  showOutline={false}
                  suffixIcon={searchStr ? CancelClose : ''}
                  suffixIconSize={IconSize.SIZE_20x20}
                  onClickSuffix={() => setSearchStr('')}
                  inputIconPadding='pl-8'
                />
              </div>

              {/* Column list */}
              <Scrollable style={{ maxHeight: '300px' }}>
                <div className='p-2'>
                  {filteredColumns.map((column) => (
                    <div key={column.id} className='py-2 px-2 hover:bg-grey1 rounded'>
                      <CheckBox
                        label={column.label}
                        selected={column.visible}
                        onChange={() => handleToggle(column.id, column.visible)}
                        isDisabled={column.visible && visibleCount === 1}
                      />
                    </div>
                  ))}
                </div>
              </Scrollable>

              {/* Reset */}
              <div className='p-2 border-t border-grey2'>
                <button
                  type='button'
                  onClick={onReset}
                  className='text-denim hover:text-denim_hover'
                >
                  <Label text={t('reset_columns')} type={LabelType.BODY3} color={ColorType.DENIM} />
                </button>
              </div>
            </div>
          </FloatingFocusManager>
        )}
      </>
    );
  },
);

ChargerColumnsDropdown.displayName = 'ChargerColumnsDropdown';
